import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Categoria {
  id: string;
  nome: string;
  slug: string;
  descricao: string | null;
  ordem: number;
}

export interface Artigo {
  id: string;
  titulo: string;
  slug: string;
  resumo: string | null;
  conteudo_html: string;
  imagem_destaque_url: string | null;
  categoria_id: string | null;
  data_publicacao: string;
  publicado: boolean;
  destaque: boolean;
  seo_title: string | null;
  seo_description: string | null;
  created_at: string;
  updated_at: string;
  categoria?: Categoria;
}

export function useCategorias() {
  return useQuery({
    queryKey: ["categorias-blog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categorias_blog")
        .select("*")
        .order("ordem");

      if (error) throw error;
      return data as Categoria[];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useArtigos(categoriaSlug?: string, page: number = 1, limit: number = 12) {
  return useQuery({
    queryKey: ["artigos", categoriaSlug, page, limit],
    queryFn: async () => {
      const now = new Date().toISOString();
      let query = supabase
        .from("artigos")
        .select(`
          *,
          categoria:categorias_blog(*)
        `, { count: "exact" })
        .eq("publicado", true)
        .lte("data_publicacao", now) // Apenas artigos com data <= agora
        .order("destaque", { ascending: false })
        .order("data_publicacao", { ascending: false });

      if (categoriaSlug) {
        const { data: categoria } = await supabase
          .from("categorias_blog")
          .select("id")
          .eq("slug", categoriaSlug)
          .single();

        if (categoria) {
          query = query.eq("categoria_id", categoria.id);
        }
      }

      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      return {
        artigos: data as Artigo[],
        total: count || 0,
        totalPages: Math.ceil((count || 0) / limit),
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useArtigo(slug: string) {
  return useQuery({
    queryKey: ["artigo", slug],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("artigos")
        .select(`
          *,
          categoria:categorias_blog(*)
        `)
        .eq("slug", slug)
        .eq("publicado", true)
        .lte("data_publicacao", now) // Apenas artigos com data <= agora
        .maybeSingle();

      if (error) throw error;
      return data as Artigo | null;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useArtigosRelacionados(categoriaId: string | null, artigoAtualId: string) {
  return useQuery({
    queryKey: ["artigos-relacionados", categoriaId, artigoAtualId],
    queryFn: async () => {
      if (!categoriaId) return [];

      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("artigos")
        .select(`
          id,
          titulo,
          slug,
          resumo,
          imagem_destaque_url,
          data_publicacao
        `)
        .eq("categoria_id", categoriaId)
        .eq("publicado", true)
        .lte("data_publicacao", now) // Apenas artigos com data <= agora
        .neq("id", artigoAtualId)
        .order("data_publicacao", { ascending: false })
        .limit(3);

      if (error) throw error;
      return data;
    },
    enabled: !!categoriaId,
    staleTime: 1000 * 60 * 5,
  });
}

export function formatarData(dataString: string): string {
  const data = new Date(dataString);
  return data.toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
